import React, { useState } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/shared/utils/shadcn";
import { Icon, IconColor, IconName } from "@/shared/ui/Icon";
import { Row, Column } from "@/shared/ui/Blocks";
import { SubtitleText } from "../Typography";

type NotificationVariant = "info" | "warning" | "success";

const variantIconColorMap: Record<NotificationVariant, IconColor> = {
  info: "blue",
  warning: "orange",
  success: "green",
};

const notificationBannerVariants = cva("relative w-full rounded-lg p-4", {
  variants: {
    variant: {
      info: "bg-blue-50",
      warning: "bg-orange-50",
      success: "bg-green-50 ",
    },
  },
  defaultVariants: {
    variant: "info",
  },
});

const titleVariants = cva("text-sm font-semibold", {
  variants: {
    variant: {
      info: "text-gray-900",
      warning: "text-gray-900",
      success: "text-gray-900",
    },
  },
  defaultVariants: {
    variant: "info",
  },
});

const descriptionVariants = cva("text-sm mt-1", {
  variants: {
    variant: {
      info: "text-gray-600",
      warning: "text-gray-600",
      success: "text-gray-600",
    },
  },
  defaultVariants: {
    variant: "info",
  },
});

const actionVariants = cva(
  "inline-flex items-center gap-1.5 text-sm font-medium mt-2 cursor-pointer hover:underline",
  {
    variants: {
      variant: {
        info: "text-blue-600",
        warning: "text-orange-600",
        success: "text-green-600",
      },
    },
    defaultVariants: {
      variant: "info",
    },
  }
);

export interface NotificationBannerAction {
  label: string;
  icon?: IconName;
  onClick?: () => void;
  href?: string;
}

export interface NotificationBannerProps
  extends VariantProps<typeof notificationBannerVariants> {
  title: string;
  description?: string;
  action?: NotificationBannerAction;
  dismissible?: boolean;
  onDismiss?: () => void;
  className?: string;
}

/**
 * NotificationBanner - A reusable notification/alert banner component
 *
 * Displays contextual messages with optional actions and dismiss functionality.
 * Supports multiple variants for different message types (info, warning, success).
 */
function NotificationBanner(props: NotificationBannerProps) {
  const {
    title,
    description,
    action,
    variant = "info",
    dismissible = false,
    onDismiss,
    className,
  } = props;

  const [isDismissed, setIsDismissed] = useState(false);

  const handleDismiss = () => {
    setIsDismissed(true);
    onDismiss?.();
  };

  if (isDismissed) {
    return null;
  }

  const ActionElement = action?.href ? "a" : "button";

  return (
    <div className={cn(notificationBannerVariants({ variant }), className)}>
      <Row className="justify-between items-start">
        <Column className="flex-1 pr-4">
          <h6 className={titleVariants({ variant })}>{title}</h6>
          {description && (
            <SubtitleText className={descriptionVariants({ variant })}>
              {description}
            </SubtitleText>
          )}
          {action && (
            <ActionElement
              className={actionVariants({ variant })}
              onClick={action.onClick}
              href={action.href}
            >
              {action.icon && (
                <Icon
                  icon={action.icon}
                  size="sm"
                  color={variantIconColorMap[variant ?? "info"]}
                />
              )}
              <span>{action.label}</span>
              {!action.icon && variant === "warning" && (
                <Icon
                  icon="Arrow"
                  size="xs"
                  color={variantIconColorMap[variant]}
                />
              )}
            </ActionElement>
          )}
        </Column>
        {dismissible && (
          <button
            type="button"
            onClick={handleDismiss}
            className="p-1 rounded hover:bg-black/5 transition-colors"
            aria-label="Dismiss notification"
          >
            <Icon icon="Close" size="sm" color="grey" />
          </button>
        )}
      </Row>
    </div>
  );
}

NotificationBanner.displayName = "NotificationBanner";

export { NotificationBanner, notificationBannerVariants };
